import { Component, signal, OnInit, OnDestroy, HostListener, computed, effect, untracked, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AuthService, FederationChoice } from '../../../core/service/auth/auth.service';
import { PermissionService } from '../../../core/service/permission/permission.service';
import { VersionService } from '../../../core/service/version-service/version.service';
import { ApiService } from '../../../core/service/api-service/api-service';
import { API } from '../../../core/API';

export interface MenuItem {
  label: string;
  icon: string;
  route: string;
  children?: MenuItem[];
  badgePulse?: boolean;
  hideBadge?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  // Tablet (768-1023px): start on the icon rail so the 256px menu does not eat half the screen.
  isOpen = signal(typeof window === 'undefined' || window.innerWidth < 768 || window.innerWidth >= 1024);
  mobileOpen = signal(false);
  currentRoute = signal('');
  expandedItems = signal<Set<string>>(new Set());
  lastActiveListRoute = signal<string | null>(null);
  // Working federation (null = none chosen): the "Fédération de travail" card and the Backoffice block follow it
  federation = inject(AuthService).currentFederation;
  // Plateforme block
  federationsCount = signal<number | null>(null);
  // logo URL that failed to load (file service unreachable / file missing): the card shows the acronym instead
  failedLogo = signal<string | null>(null);
  // Switcher dialog (card "Choisir une fédération" / "Changer")
  switcherOpen = signal(false);
  switcherLoading = signal(false);
  switcherError = signal<string | null>(null);
  switcherSearch = signal('');
  switcherItems = signal<FederationChoice[]>([]);
  switching = signal<number | null>(null);
  switcherFiltered = computed(() => {
    const t = this.switcherSearch().trim().toLowerCase();
    const all = this.switcherItems();
    return t ? all.filter(f => `${f.federation_acornyme} ${f.federation_name}`.toLowerCase().includes(t)) : all;
  });
  leaving = signal(false);
  version = signal<string>('');
  menuItems = signal<MenuItem[]>([]);
  currentSeasonLabel = signal<string>('');

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private versionService: VersionService,
    private api: ApiService,
    private permService: PermissionService,
  ) {
    this.version.set(this.versionService.getVersion());
    // The backoffice menu is built from the working federation's licence periods: rebuild it when the federation changes.
    effect(() => {
      const id = this.federation()?.federation_id;
      untracked(() => id ? this.loadLicencePeriods() : this.menuItems.set([]));
    });
  }

  ngOnInit(): void {
    this.updateRouteState(this.router.url);
    this.loadFederationsCount();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((e: any) => {
        this.updateRouteState(e.urlAfterRedirects);
        // back on the list after a create / delete: refresh the count
        if (e.urlAfterRedirects === '/plateforme/federations') this.loadFederationsCount();
      });
  }

  hasPlatformRights(): boolean {
    return this.authService.hasPlatformRights();
  }

  canCreateFederation(): boolean {
    return this.permService.can('main', 'federations', 'create');
  }

  canReadPlatform(module: 'platform_users' | 'platform_roles'): boolean {
    return this.permService.can('main', module, 'read');
  }

  isRouteActive(route: string): boolean {
    const url = this.currentRoute().split('?')[0];
    return url === route || url.startsWith(route + '/');
  }

  private loadFederationsCount(): void {
    if (!this.hasPlatformRights()) return;
    this.api.get(API.PLATFORM.FEDERATIONS).pipe(takeUntil(this.destroy$)).subscribe({
      next: (r) => this.federationsCount.set((r.data ?? []).length),
      error: () => this.federationsCount.set(null),
    });
  }

  // ── Plateforme block ──
  isPlatformListActive(): boolean {
    const url = this.currentRoute().split('?')[0];
    return url.startsWith('/plateforme/federations') && url !== '/plateforme/federations/nouvelle';
  }

  isPlatformCreateActive(): boolean {
    return this.currentRoute().split('?')[0] === '/plateforme/federations/nouvelle';
  }

  go(route: string): void {
    this.closeMobile();
    this.router.navigateByUrl(route);
  }

  // ── Switcher dialog ──
  openSwitcher(): void {
    this.switcherOpen.set(true);
    this.switcherSearch.set('');
    this.switcherError.set(null);
    this.switcherLoading.set(true);
    this.authService.listFederations().pipe(takeUntil(this.destroy$)).subscribe({
      next: (r) => { this.switcherItems.set(r.data || []); this.switcherLoading.set(false); },
      error: () => { this.switcherError.set('Impossible de charger la liste des fédérations.'); this.switcherLoading.set(false); },
    });
  }

  @HostListener('document:keydown.escape')
  closeSwitcher(): void {
    if (this.switching() === null) this.switcherOpen.set(false);
  }

  chooseFederation(f: FederationChoice): void {
    if (this.switching() !== null) return;
    if (f.federation_id === this.federation()?.federation_id) {
      this.switcherOpen.set(false);
      this.go('/federation/dashboard');
      return;
    }
    this.switcherError.set(null);
    this.switching.set(f.federation_id);
    this.authService.selectFederation(f.federation_id).subscribe({
      next: () => {
        this.switching.set(null);
        this.switcherOpen.set(false);
        this.authService.clearLostFederationNotice();
        this.go('/federation/dashboard');
      },
      error: (e) => {
        this.switching.set(null);
        this.switcherError.set(e.error?.message || "Impossible d'ouvrir cette fédération.");
      },
    });
  }

  /** "Quitter": back to the Plateforme zone. Navigate first so no backoffice page reloads without a federation. */
  leaveFederation(): void {
    if (this.leaving()) return;
    this.leaving.set(true);
    this.closeMobile();
    this.router.navigateByUrl('/plateforme/federations').then(ok => {
      if (!ok) { this.leaving.set(false); return; }
      this.authService.leaveFederation().subscribe({
        next: () => this.leaving.set(false),
        error: () => this.leaving.set(false),
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadLicencePeriods(): void {
    this.api.get(`${API.FEDERATION.COMPAGNES_LIST}?page=1&limit=1000`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.buildFederationMenu(response.data || []),
        error: () => this.buildFederationMenu([]),
      });
  }

  private buildFederationMenu(licencePeriods: any[]): void {
    const menuItems: MenuItem[] = [
      { label: 'Tableau de bord', icon: 'dashboard', route: '/federation/dashboard' },
    ];

    const isOpen = (p: any): boolean => {
      const s = String(p?.licenceperiod_status || '').trim().toLowerCase();
      return s === 'ouvert' || s === 'open';
    };

    const isCompnl = (p: any): boolean => {
      const type = String(p?.licenceperiod_type || '').trim();
      if (type) return type === 'COMPNL';
      const name = String(p?.licenceperiod_name || '').toLowerCase();
      return !name.includes('transfert') && !name.includes('transfer');
    };

    licencePeriods.filter(isCompnl).forEach(p => {
      const children: MenuItem[] = [
        { label: 'Clubs',       icon: 'apartment',         route: '/federation/clubs' },
        { label: 'Joueurs',     icon: 'people',            route: '/federation/joueurs' },
        { label: 'Dirigeants',  icon: 'supervisor_account',route: '/federation/dirigeants/list' },
        { label: 'Entraîneurs', icon: 'group',             route: '/federation/staff/list' },
        { label: 'Arbitres',    icon: 'how_to_vote',       route: '/federation/referees' },
      ];
      menuItems.push({ label: p.licenceperiod_name || 'Campagne', icon: 'card_membership', route: '', badgePulse: isOpen(p), children });
    });

    // Licences — real-data lists (generated/printed licences from the fiche).
    // Season label derived from the current competition period (same tab_sportseason the licences use).
    const compnl = licencePeriods.filter(isCompnl);
    const currentPeriod = compnl.find(isOpen) || compnl[0];
    const rawSeason = currentPeriod?.sportseason_long_label
      || (currentPeriod?.sportseason_full_code ? String(currentPeriod.sportseason_full_code).replace(/^SE/i, '20') : '');
    const seasonYear = (String(rawSeason).match(/\d{4}/) || [])[0] || rawSeason;
    this.currentSeasonLabel.set(seasonYear || '');
    const seasonSuffix = seasonYear ? ` ${seasonYear}` : '';
    menuItems.push({
      label: `Licenciés${seasonSuffix}`, icon: 'badge', route: '',
      children: [
        { label: 'Joueurs', icon: 'people', route: '/federation/licences/joueurs' },
        { label: 'Dirigeants', icon: 'manage_accounts', route: '/federation/licences/dirigeants' },
        { label: 'Entraîneurs', icon: 'sports', route: '/federation/licences/entraineurs' },
        { label: 'Arbitres', icon: 'sports_score', route: '/federation/licences/arbitres' },
      ],
    });

    menuItems.push({
      label: 'Gestion', icon: 'settings', route: '', hideBadge: true,
      children: [
        { label: 'Gestion du menu', icon: 'menu_open', route: '/federation/menu' },
        { label: 'Formulaires', icon: 'description', route: '/federation/formulaires' },
      ],
    });

    this.menuItems.set(menuItems);
  }

  private updateRouteState(url: string): void {
    this.currentRoute.set(url);
    const childRoutes = this.menuItems().flatMap(i => i.children || []).map(c => c.route).filter(Boolean);
    const best = this.findBestMatch(url, childRoutes);
    if (best) this.lastActiveListRoute.set(best);
  }

  toggleSidebar(): void { this.isOpen.update(s => !s); }
  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
    // The phone drawer always shows labels, never the icon rail.
    if (this.mobileOpen() && !this.isOpen()) this.isOpen.set(true);
  }
  closeMobile(): void { this.mobileOpen.set(false); }

  navigateTo(item: MenuItem): void {
    if (!item.route || item.route === '') return;
    this.closeMobile();
    this.router.navigate([item.route]);
  }

  isActive(route: string): boolean {
    return this.findBestMatch(this.currentRoute(), this.getAllNavigableRoutes()) === route;
  }

  isChildActive(route: string): boolean {
    const current = this.currentRoute();
    const best = this.findBestMatch(current, this.getAllNavigableRoutes());
    if (best === route) return true;
    const known = this.getAllNavigableRoutes().some(r => current === r);
    if (known) return false;
    return this.lastActiveListRoute() === route;
  }

  private getAllNavigableRoutes(): string[] {
    return [
      ...this.menuItems().map(m => m.route),
      ...this.menuItems().flatMap(m => m.children?.map(c => c.route) || []),
    ].filter((r): r is string => Boolean(r) && r !== '');
  }

  private findBestMatch(current: string, routes: string[]): string | null {
    let best: string | null = null;
    for (const r of routes) {
      if (current === r || current.startsWith(r + '/')) {
        if (!best || r.length > best.length) best = r;
      }
    }
    return best;
  }
}
